"use client";

import { type FC, type HTMLAttributes, type ReactNode, useState } from "react";
import { AnimatePresence } from "motion/react";
import { useTranslation } from "react-i18next";

import { IconButton } from "../IconButton/IconButton";
import { Group, type GroupProps } from "../Group/Group";
import { Tooltip } from "../Tooltip/Tooltip";
import { Modal } from "../Modal/Modal";
import { Form } from "../Form/Form";
import { FormRow } from "../FormRow/FormRow";
import { TextArea } from "../TextArea/TextArea";
import { Select } from "../Select/Select";
import type {
  SelectListOption,
  SelectListValue,
} from "../SelectList/SelectList";
import { Icon } from "../Icon/Icon";
import {
  ButtonToggle,
  type ButtonToggleValue,
} from "../ButtonToggle/ButtonToggle";
import { ProgressBar } from "../ProgressBar/ProgressBar";
import { SubmitButton } from "../Button/SubmitButton";
import { Flex } from "../Flex/Flex";

export type ResponseFeedbackData = {
  correctness: number;
  tags: string[];
  comments: string;
};

export type ResponseFeedbackProps = Omit<
  HTMLAttributes<HTMLDivElement>,
  "onSubmit"
> & {
  loading?: boolean;
  disabled?: boolean;
  comments?: boolean;
  border?: boolean;
  direction?: GroupProps["direction"];
  tags?: boolean;
  tagOptions?: Record<string, string>;
  className?: string;
  modalTrailing?: ReactNode;
  onSubmit?: (data: ResponseFeedbackData, close: () => void) => void;
};

/**
 * A user feedback component that allows the user to give feedback on the AI response message.
 *
 * @todo Send GIT hash and front-end version to backend?
 */
export const ResponseFeedback: FC<ResponseFeedbackProps> = ({
  loading = false,
  comments: hasComments = false,
  tags: hasTags = false,
  border = true,
  disabled = false,
  tagOptions = {},
  direction = "horizontal",
  modalTrailing,
  className,
  onSubmit,
  ...props
}) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [correctness, setCorrectness] = useState<ButtonToggleValue | null>(
    null,
  );
  const [comments, setComments] = useState("");
  const [tags, setTags] = useState<SelectListValue>([]);

  const handleFeedbackGoodClick = () => {
    setIsModalOpen(true);
    setCorrectness(1);
  };
  const handleFeedbackBadClick = () => {
    setIsModalOpen(true);
    setCorrectness(0);
  };

  const handleSubmit = () => {
    onSubmit?.(
      {
        correctness: correctness as number,
        tags: tags as string[],
        comments,
      },
      () => setIsModalOpen(false),
    );
  };

  const tagOptionsArray: SelectListOption[] = Object.entries(tagOptions).map(
    ([key, value]) => ({
      value: key,
      label: value,
    }),
  );
  return (
    <>
      <Group className={className} direction={direction} {...props}>
        <Tooltip
          value={t("feedback.bad.long")}
          placement="topRight"
          disabled={disabled}
        >
          <IconButton
            icon={correctness === 0 ? "thumbsDownFilled" : "thumbsDown"}
            size="xxs"
            rounded
            variant="opaque"
            border={border ? "input" : undefined}
            onClick={handleFeedbackBadClick}
            disabled={disabled}
          />
        </Tooltip>
        <Tooltip
          value={t("feedback.good.long")}
          disabled={disabled}
          placement="topRight"
        >
          <IconButton
            icon={correctness === 1 ? "thumbsUpFilled" : "thumbsUp"}
            size="xxs"
            rounded
            variant="opaque"
            border={border ? "input" : undefined}
            onClick={handleFeedbackGoodClick}
            disabled={disabled}
          />
        </Tooltip>
      </Group>

      <AnimatePresence>
        {isModalOpen && (
          <Modal
            title={t("feedback.title")}
            placement="center"
            onClose={() => setIsModalOpen(false)}
            closeDisabled={loading}
            closeable
          >
            {loading && <ProgressBar position="absolute" variant="infinite" />}

            <Form disabled={loading} onSubmit={handleSubmit}>
              <FormRow label={t("feedback.score")}>
                <ButtonToggle
                  options={[
                    {
                      value: 0,
                      label: t("feedback.bad.short"),
                      leading: (
                        <Icon
                          icon={
                            correctness === 0
                              ? "thumbsDownFilled"
                              : "thumbsDown"
                          }
                          size="s2"
                        />
                      ),
                    },
                    {
                      value: 1,
                      label: t("feedback.good.short"),
                      leading: (
                        <Icon
                          icon={
                            correctness === 1 ? "thumbsUpFilled" : "thumbsUp"
                          }
                          size="s2"
                        />
                      ),
                    },
                  ]}
                  value={correctness ?? undefined}
                  onChange={setCorrectness}
                />
              </FormRow>

              {hasTags && tagOptions && (
                <FormRow label={t("feedback.tags")}>
                  <Select
                    value={tags}
                    onChange={setTags}
                    options={tagOptionsArray}
                    placeholder={t("general.select")}
                    multi
                  />
                </FormRow>
              )}

              {hasComments && (
                <FormRow label={t("feedback.message")} variant="vertical">
                  <TextArea
                    value={comments}
                    onChange={setComments}
                    autoFocus
                    validation={["required"]}
                  />
                </FormRow>
              )}

              <Flex justify="space-between" gap="s" align="center">
                <SubmitButton variant="secondary" size="s">
                  {t("feedback.submit")}
                </SubmitButton>
                {modalTrailing}
              </Flex>
            </Form>
          </Modal>
        )}
      </AnimatePresence>
    </>
  );
};
